import axios, { AxiosResponse } from 'axios';
import { useLoadingStore } from '@src/store/common/LoadingStore';
import { useOverlay } from '@abyss/web/hooks/useOverlay';
import { CookieService } from './CookieService';
import { acquireTokenSilently } from './MsalService';
import { ApiMessages } from '@src/constants/ApiMessage';
import { ApiStatus } from '@src/constants/ApiStatus';
import { useRouter } from '@abyss/web/hooks/useRouter';

export type ApiResponse = {
  data: any;
  message: string;
  status?: number;
};

const HttpService = () => {
  const loadingState = useLoadingStore((state) => state.loadingState);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const loadingOverlay = useOverlay('crdp-loading');
  const { navigate } = useRouter();

  // Async: attempts MSAL silent refresh first, falls back to cookie.
  // withCredentials is intentionally NOT set — Bearer token auth does not
  // require it. Setting it would force the browser into credentials mode,
  // requiring the Flask backend to set Access-Control-Allow-Credentials: true
  // and forbid Access-Control-Allow-Origin: * causing CORS preflight failures.
  const GetHeaders = async (
    contentType: string = 'application/json'
  ): Promise<any> => {
    let token: string | null = null;

    try {
      const tokenResult = await acquireTokenSilently();
      if (tokenResult?.accessToken) {
        token = tokenResult.accessToken;
        // Keep cookie in sync with the freshly acquired token
        CookieService.SaveCookie('authToken', token);
      }
    } catch {
      // MSAL unavailable or interaction required — fall through to cookie
    }

    if (!token) {
      token = CookieService.GetCookie('authToken') ?? null;
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': contentType,
      },
      // withCredentials: false is the default — do NOT set to true.
      // Reason: we use Authorization header (Bearer), not browser-managed cookies.
    };
  };

  const response: ApiResponse = { data: null, message: '', status: 0 };

  const ErrorResponse = (err: any): ApiResponse => {
    response.data = null;
    if (err.response?.status === 401) {
      response.message = ApiMessages.SessionExpired;
      response.status = 401;
      navigate('/login');
    } else if (err.response?.status === 403) {
      response.message = ApiMessages.AccessDenied;
      response.status = 403;
      navigate('/access-denied');
    } else {
      response.message = err.message;
      response.status = err.response?.status;
    }

    return response;
  };

  const Response = (res: AxiosResponse<any, any>): ApiResponse => {
    if (
      res.status === 200 &&
      res.data?.hasError === false &&
      res.data?.hasWarning === false
    ) {
      response.data = res.data;
      response.message = ApiMessages.Success;
      response.status = res.status;
    } else if (
      res.status === 200 &&
      (res.data?.hasError === true || res.data?.hasWarning === true)
    ) {
      response.data = res.data;
      response.message = ApiMessages.Error;
      response.status = ApiStatus.Error;
    } else {
      response.data = res.data;
      response.message = ApiMessages.Error;
      response.status = res.status;
    }
    return response;
  };

  const GetLoaderState = (isLoading: boolean) => {
    if (isLoading === true) {
      loadingState.count += 1;
    } else {
      loadingState.count -= 1;
    }
    loadingState.loading = loadingState.count > 0;
    if (loadingState.loading === true) {
      loadingOverlay.open();
    } else {
      loadingOverlay.close();
    }
    return loadingState;
  };

  const SanitizePost = (payload: any): any => {
    delete payload.createdBy;
    delete payload.createdDate;
    delete payload.updatedBy;
    delete payload.updatedDate;
    return payload;
  };

  const UpdateLoaderFinally = async (loaderState, showLoader: boolean) => {
    if (showLoader === true) {
      loaderState = GetLoaderState(false);
      await setLoading(loaderState);
    }
  };

  const HttpPost = async (
    url: string,
    payload: any,
    showLoader: boolean = true,
    contentType?: string
  ): Promise<ApiResponse> => {
    let loaderState;
    const headers = await GetHeaders(contentType);
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      payload = SanitizePost(payload);
      const res = await axios.post(url, payload, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpGet = async (
    url: string,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    const headers = await GetHeaders();
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      const res = await axios.get(url, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpDelete = async (
    url: string,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    const headers = await GetHeaders();
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      const res = await axios.delete(url, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  const HttpPut = async (
    url: string,
    payload: any,
    showLoader: boolean = true
  ): Promise<ApiResponse> => {
    let loaderState;
    const headers = await GetHeaders();
    if (showLoader === true) {
      loaderState = GetLoaderState(true);
      await setLoading(loaderState);
    }
    try {
      payload = SanitizePost(payload);
      const res = await axios.put(url, payload, headers);
      return Response(res);
    } catch (error) {
      return ErrorResponse(error);
    } finally {
      await UpdateLoaderFinally(loaderState, showLoader);
    }
  };

  return { HttpPost, HttpGet, HttpDelete, HttpPut };
};

export { HttpService };
